import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import { pipes, type CardPriority, type PipeStep } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

/**
 * Prioridades do cartao das esteiras (GetAllKanbanCardPriority do OnPoint, P-EST-1).
 *
 * A tabela tem nivel: o maior e o mais urgente, e desempata o quadro. As quatro do
 * SeguraCore tem a chave da prioridade da operacao, e a prioridade nova so existe no
 * cartao. A empresa tem sempre uma padrao, a do cartao novo.
 */
export function CardPrioritiesPanel() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const podeVer = can('esteiras.esteira.view');
  const podeEditar = can('esteiras.configuracao.update');

  const [nome, setNome] = useState('');
  const [nivel, setNivel] = useState('1');

  const consulta = useQuery({
    queryKey: ['pipes', 'priorities'],
    queryFn: ({ signal }) => pipes.priorities(true, signal),
    enabled: podeVer,
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['pipes', 'priorities'] });
  }

  const criar = useMutation({
    mutationFn: () => pipes.createPriority({ name: nome.trim(), level: Number(nivel) }),
    onSuccess: () => {
      toast.success('Prioridade criada.');
      setNome('');
      setNivel('1');
      invalidar();
    },
  });

  const atualizar = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CardPriority> }) =>
      pipes.updatePriority(id, input),
    onSuccess: () => {
      toast.success('Prioridade atualizada.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  if (!podeVer) return null;

  const apiError = criar.error instanceof ApiError ? criar.error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    criar.mutate();
  }

  return (
    <Card>
      <CardHeader
        title="Prioridades"
        description="Prioridades do cartao das esteiras, da mais urgente para a menos."
      />
      <CardBody className="flex flex-col gap-3">
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar as prioridades">
            {consulta.error.message}
          </Alert>
        ) : null}

        {consulta.isPending ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
            {(consulta.data ?? []).map((prioridade) => (
              <li
                key={prioridade.id}
                className="flex flex-wrap items-center gap-3 px-3 py-2.5"
              >
                <span
                  className={
                    prioridade.active
                      ? 'min-w-0 flex-1 truncate text-sm text-ink-800'
                      : 'min-w-0 flex-1 truncate text-sm text-ink-400 line-through'
                  }
                >
                  {prioridade.name}
                </span>
                <span className="text-2xs text-ink-500">Nivel {prioridade.level}</span>
                {prioridade.is_default ? <Badge tone="brand">Padrao</Badge> : null}
                {!prioridade.active ? <Badge tone="neutral">Inativa</Badge> : null}
                {podeEditar && !prioridade.is_default && prioridade.active ? (
                  <AcaoComIcone
                    acao="tornarPrincipal"
                    rotulo="Tornar padrao"
                    disabled={atualizar.isPending}
                    onClick={() =>
                      atualizar.mutate({
                        id: prioridade.id,
                        input: { is_default: true },
                      })
                    }
                  />
                ) : null}
                {podeEditar && !prioridade.is_default ? (
                  <AcaoComIcone
                    acao={prioridade.active ? 'desativar' : 'reativar'}
                    rotulo={prioridade.active ? 'Desativar' : 'Reativar'}
                    disabled={atualizar.isPending}
                    onClick={() =>
                      atualizar.mutate({
                        id: prioridade.id,
                        input: { active: !prioridade.active },
                      })
                    }
                  />
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {podeEditar ? (
          <form onSubmit={enviar} className="grid gap-3 sm:grid-cols-[1fr_8rem_auto]">
            <Field
              label="Nome da prioridade"
              value={nome}
              maxLength={60}
              error={fieldErrorOf(apiError, 'name')}
              onChange={(evento) => setNome(evento.target.value)}
            />
            <Field
              label="Nivel"
              type="number"
              min="0"
              max="99"
              value={nivel}
              error={fieldErrorOf(apiError, 'level')}
              onChange={(evento) => setNivel(evento.target.value)}
            />
            <div className="flex items-end">
              <Button
                type="submit"
                loading={criar.isPending}
                disabled={nome.trim() === ''}
              >
                Criar prioridade
              </Button>
            </div>
            {criar.isError && !apiError?.details?.field ? (
              <Alert tone="danger" title="Nao foi possivel criar">
                {criar.error.message}
              </Alert>
            ) : null}
          </form>
        ) : (
          <p className="text-2xs text-ink-500">
            Somente leitura: o seu acesso nao inclui a configuracao das esteiras.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Prazo por coluna das esteiras (P-EST-1).
 *
 * O cartao que entra na coluna vence pelo prazo dela; sem prazo, o cartao de
 * operacao acompanha o prazo da etapa da operacao, acima. A derrubada automatica do
 * OnPoint (60, 25 e 15 dias) e regra pendente e conflita com o art. 49 da Lei
 * 15.040/2024: ela nao liga.
 */
export function PipeStepSlaPanel() {
  const { can } = useAuth();
  const podeVer = can('esteiras.esteira.view');
  const [esteira, setEsteira] = useState('');

  const lista = useQuery({
    queryKey: ['pipes'],
    queryFn: ({ signal }) => pipes.list(signal),
    enabled: podeVer,
  });
  const escolhida = esteira || lista.data?.[0]?.id || '';
  const detalhe = useQuery({
    queryKey: ['pipes', escolhida],
    queryFn: ({ signal }) => pipes.get(escolhida, signal),
    enabled: podeVer && escolhida !== '',
  });

  if (!podeVer) return null;

  return (
    <Card>
      <CardHeader
        title="Prazo por coluna da esteira"
        description="Vence o prazo da etapa quando a coluna tem prazo proprio."
      />
      <CardBody className="flex flex-col gap-3">
        <Select
          label="Esteira"
          className="h-9 max-w-xs"
          options={(lista.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
          value={escolhida}
          onChange={(evento) => setEsteira(evento.target.value)}
        />
        {detalhe.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar as colunas">
            {detalhe.error.message}
          </Alert>
        ) : null}
        {detalhe.isPending ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
            {(detalhe.data?.steps ?? []).map((step) => (
              <PrazoDaColuna key={step.id} step={step} />
            ))}
          </ul>
        )}
        <p className="text-2xs text-ink-500">
          Dias uteis contam de segunda a sexta, sem feriado; o prazo vence no fim do
          ultimo dia. A derrubada automatica da proposta segue desligada (regra
          pendente).
        </p>
      </CardBody>
    </Card>
  );
}

function PrazoDaColuna({ step }: { step: PipeStep }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeEditar = can('esteiras.configuracao.update');

  const [dias, setDias] = useState(step.sla_days ? String(step.sla_days) : '');
  const [contagem, setContagem] = useState<'uteis' | 'corridos'>(step.sla_calendar);

  const salvar = useMutation({
    mutationFn: () =>
      pipes.updateStepSla(step.id, {
        sla_days: dias.trim() === '' ? null : Number(dias),
        sla_calendar: contagem,
      }),
    onSuccess: () => {
      toast.success(`Prazo de ${step.name} definido.`);
      void queryClient.invalidateQueries({ queryKey: ['pipes'] });
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const alterado =
    (dias.trim() === '' ? null : Number(dias)) !== step.sla_days ||
    contagem !== step.sla_calendar;

  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2.5">
      <span className="min-w-0 flex-1 truncate text-sm text-ink-700">{step.name}</span>
      {step.is_final ? <Badge tone="neutral">Final</Badge> : null}
      <div className="w-24 shrink-0">
        <Field
          label={`Prazo em dias de ${step.name}`}
          labelHidden
          type="number"
          min="1"
          max="365"
          className="h-9"
          placeholder="sem prazo"
          disabled={!podeEditar || step.is_final}
          value={dias}
          onChange={(evento) => setDias(evento.target.value)}
        />
      </div>
      <div className="w-32 shrink-0">
        <Select
          label={`Contagem do prazo de ${step.name}`}
          labelHidden
          className="h-9"
          disabled={!podeEditar || step.is_final}
          options={[
            { value: 'uteis', label: 'Dias uteis' },
            { value: 'corridos', label: 'Dias corridos' },
          ]}
          value={contagem}
          onChange={(evento) =>
            setContagem(evento.target.value as 'uteis' | 'corridos')
          }
        />
      </div>
      {podeEditar && !step.is_final ? (
        <Button
          size="sm"
          variant="secondary"
          disabled={!alterado || salvar.isPending}
          onClick={() => salvar.mutate()}
        >
          Salvar
        </Button>
      ) : null}
    </li>
  );
}
